import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterOutlet, RouterLinkActive, ActivatedRoute } from '@angular/router';

interface MenuItem {
    label: string;
    route: string;
    icon?: string;
}

interface MenuSection {
    title: string;
    items: MenuItem[];
}

@Component({
    selector: 'app-project-layout',
    standalone: true,
    imports: [CommonModule, RouterLink, RouterOutlet, RouterLinkActive],
    template: `
        <div class="project-layout">
            <aside class="sidebar">
                <div class="sidebar-header">
                    <h2>{{ projectName }}</h2>
                </div>

                <nav class="sidebar-nav">
                    <div class="nav-item">
                        <a
                            [routerLink]="['./']"
                            routerLinkActive="active"
                            [routerLinkActiveOptions]="{ exact: true }"
                        >
                            <span>Home</span>
                        </a>
                    </div>

                    @for (section of menuSections; track section.title) {
                        <div class="nav-section">
                            <div class="section-title">{{ section.title }}</div>
                            @for (item of section.items; track item.route) {
                                <div class="nav-item">
                                    <a [routerLink]="[item.route]" routerLinkActive="active">
                                        <span>{{ item.label }}</span>
                                    </a>
                                </div>
                            }
                        </div>
                    }
                </nav>
            </aside>

            <main class="main-content">
                <router-outlet></router-outlet>
            </main>
        </div>
    `,
    styles: [
        `
            .project-layout {
                display: flex;
                height: 100vh;
                background: #f5f5f5;
            }

            .sidebar {
                width: 260px;
                background: white;
                border-right: 1px solid #e0e0e0;
                display: flex;
                flex-direction: column;
                flex-shrink: 0;
            }

            .sidebar-header {
                padding: 1.5rem;
                border-bottom: 1px solid #e0e0e0;
            }

            .sidebar-header h2 {
                font-size: 1.25rem;
                font-weight: 600;
                color: #1a1a1a;
                margin: 0;
            }

            .sidebar-nav {
                flex: 1;
                overflow-y: auto;
                padding: 1rem 0;
            }

            .nav-item {
                margin: 0 0.75rem 0.25rem;
            }

            .nav-item a {
                display: flex;
                align-items: center;
                padding: 0.75rem 1rem;
                border-radius: 6px;
                text-decoration: none;
                color: #666;
                transition: all 0.2s;
            }

            .nav-item a:hover {
                background: #f5f5f5;
                color: #1a1a1a;
            }

            .nav-item a.active {
                background: #e3f2fd;
                color: #2196f3;
                font-weight: 500;
            }

            .nav-icon {
                font-size: 1.1rem;
                width: 20px;
                text-align: center;
            }

            .nav-section {
                margin: 1.5rem 0;
            }

            .section-title {
                font-size: 0.75rem;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: #999;
                padding: 0 1.75rem;
                margin-bottom: 0.5rem;
            }

            .main-content {
                flex: 1;
                overflow-y: auto;
                background: #f5f5f5;
            }
        `,
    ],
})
export class ProjectLayout implements OnInit {
    private route = inject(ActivatedRoute);

    projectId = '';
    projectName = 'Project Name';

    menuSections: MenuSection[] = [
        {
            title: 'Configuration',
            items: [
                { label: 'Agents', route: 'agents' },
                { label: 'Roles', route: 'roles' },
                { label: 'Workflows', route: 'workflows' },
                { label: 'Tools', route: 'tools' },
            ],
        },
        {
            title: 'Security',
            items: [{ label: 'Access & Permissions', route: 'access-permissions' }],
        },
    ];

    ngOnInit() {
        this.route.paramMap.subscribe((params) => {
            this.projectId = params.get('projectId') || '';
            // In a real app, fetch project details here
            this.projectName = `Project ${this.projectId}`;
        });
    }
}
